import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';

import { MetricCardsSkeleton, UsageChartSkeleton } from '@/components/usage-skeletons/usage-skeletons';
import { appRoutes } from '@/constants/routes';

import styles from './user-details-page.module.scss';

// Kept out of the lazy user-details chunk so the route skeleton can show the page's loading state without loading chart code.

// The page titles a user it has not loaded yet with the id from the path.
export const UserDetailsPageSkeleton = ({ userId }: { userId: string }) => (
  <div className={styles.page}>
    <UserDetailsHeader title={userId} />
    <AccountSummarySkeleton />
    <BillingSkeleton />
    <QuotaTilesSkeleton />
    <UsageMetricsSkeleton />
    <UserUsageChartSkeleton />
    <PaymentsSkeleton />
    <AiHistorySkeleton />
  </div>
);

export const UserDetailsHeader = ({ title }: { title: string }) => (
  <div className={styles.header}>
    <div>
      <Link className={styles.backLink} to={appRoutes.users}>
        Users
      </Link>
      <h2 className={styles.title}>{title}</h2>
    </div>
  </div>
);

const ACCOUNT_SUMMARY_LABELS = ['User ID', 'Email', 'Joined'];

export const AccountSummarySkeleton = () => (
  <section className={styles.section}>
    <div className={styles.accountHeader}>
      <h3 className={styles.sectionTitle}>Account</h3>
    </div>
    <dl className={styles.detailGrid} data-testid="account-summary-skeleton">
      {ACCOUNT_SUMMARY_LABELS.map((label) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>
            <Skeleton width={180} height={17} />
          </dd>
        </div>
      ))}
    </dl>
  </section>
);

export const BillingSkeleton = () => (
  <section className={styles.section}>
    <div className={styles.sectionHeader}>
      <h3 className={styles.sectionTitle}>Billing</h3>
    </div>
    <dl className={styles.detailGrid} data-testid="billing-skeleton">
      <div>
        <dt>
          <Skeleton width={60} height={12} />
        </dt>
        <dd>
          <Skeleton width={120} height={20} />
        </dd>
      </div>
    </dl>
  </section>
);

const QUOTA_TILES = [
  { label: 'AI Preview quota', className: styles.previewTile },
  { label: 'Monthly quota', className: styles.previewTile },
  { label: 'Joule balance', className: styles.balanceTile },
];

export const QuotaTilesSkeleton = () => (
  <section className={styles.quotaGrid}>
    {QUOTA_TILES.map(({ label, className }) => (
      <div key={label} className={clsx(styles.quotaTile, className)} data-testid="quota-tile-skeleton">
        <span className={styles.metricLabel}>{label}</span>
        <Skeleton width={160} height={26} />
        <div className={styles.quotaBar} />
      </div>
    ))}
  </section>
);

const USAGE_METRIC_COUNT = 3;

export const UsageMetricsSkeleton = () => (
  <MetricCardsSkeleton count={USAGE_METRIC_COUNT} testId="usage-metric-skeleton" styles={styles} />
);

export const UserUsageChartSkeleton = () => <UsageChartSkeleton height={280} styles={styles} />;

const PAYMENT_SKELETON_ROWS = 3;

export const PaymentsSkeleton = () => (
  <section className={styles.section}>
    <div className={styles.sectionHeader}>
      <h3 className={styles.sectionTitle}>Payments</h3>
    </div>
    <div className={styles.paymentsSkeleton} data-testid="payments-skeleton">
      {Array.from({ length: PAYMENT_SKELETON_ROWS }, (_, index) => (
        <Skeleton key={index} width="100%" height={21} />
      ))}
    </div>
  </section>
);

const AI_HISTORY_SKELETON_ROWS = 3;

export const AiHistorySkeleton = () => (
  <section className={styles.section}>
    <div className={styles.sectionHeader}>
      <h3 className={styles.sectionTitle}>AI History</h3>
    </div>
    <div className={styles.aiHistorySkeleton} data-testid="ai-history-skeleton">
      {Array.from({ length: AI_HISTORY_SKELETON_ROWS }, (_, index) => (
        <Skeleton key={index} width="100%" height={21} />
      ))}
    </div>
  </section>
);
